import { createContext, ReactNode, RefObject, useCallback, useLayoutEffect, useState } from 'react';

const HorizontalScrollContext = createContext({ horizontalScroll: false, validateHorizontalOverflow: () => {} });
export default HorizontalScrollContext;

interface HorizontalScrollContextProps {
  containerRef: RefObject<HTMLDivElement>;
  tableRef: RefObject<HTMLTableElement>;
  children: ReactNode;
}

export function HorizontalScrollContextProvider({ containerRef, tableRef, children }: HorizontalScrollContextProps) {
  const [horizontalScroll, setHorizontalScroll] = useState(false);

  const validateHorizontalOverflow = useCallback(() => {
    if (containerRef.current && tableRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const contentWidth = tableRef.current.scrollWidth;

      setHorizontalScroll(contentWidth + (containerWidth - containerRef.current.clientWidth) > containerWidth);
    }
  }, [containerRef, tableRef]);

  useLayoutEffect(() => {
    validateHorizontalOverflow();

    window.addEventListener('resize', validateHorizontalOverflow);

    const resizeObserver = new ResizeObserver(validateHorizontalOverflow);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', validateHorizontalOverflow);
      resizeObserver.disconnect();
    };
  }, [containerRef, validateHorizontalOverflow]);

  return (
    <HorizontalScrollContext.Provider value={{ horizontalScroll, validateHorizontalOverflow }}>
      {children}
    </HorizontalScrollContext.Provider>
  );
}
